import type { Constant, Nil } from '@pbt/pbt-ui-components'
import { LanguageUtils } from '@pbt/pbt-ui-components'

import { RxType } from '~/api/graphql/generated/types'
import { getCommonPackagingLabel } from '~/components/dashboard/admin/catalog/inventory/inventoryUtils'
import { getHighValue } from '~/components/dashboard/invoices/invoiceUtils'
import i18n from '~/locales/i18n'
import type {
  Prescription,
  PrescriptionDrugInfo,
} from '~/types/entities/prescriptions'
import { getManualInputSelectValue } from '~/utils'
import { getPrescriptionQuantityUnits } from '~/utils/prescriptionUtils'

const REFILL_SEPARATOR = ' | '
const GENERIC_UNIT_TYPES = ['unit', 'item', 'other'] as const

export type VetDietConstants = {
  InventoryProductSizeUnit: Constant[]
  PackageType: Constant[]
  ProductForm: Constant[]
}

export interface QuantityAndRefillsOptions {
  /** When false, return only quantity + unit (no refills). Used for order table. Default true. */
  includeRefills?: boolean
  /** Parent prescription for refills to inherit data from */
  parentRx?: Prescription
  prescriptionProductSizeUnit?: Constant[]
  productSizeUnit?: Constant[]
  /** When true, return only quantity + unit (no vet diet packaging label). Used for order table. Default false. */
  quantityAndUnitOnly?: boolean
  /** When true, use quantityPerRefill instead of totalQuantity. For order table dispensed display. Default false. */
  usePerRefillQuantity?: boolean
  /** Constants needed for vet diet packaging labels */
  vetDietConstants?: VetDietConstants
}

/** Form id from GraphQL form object or normalized formId */
const getFormId = (
  drugInfo: PrescriptionDrugInfo | undefined,
): string | Nil => {
  if (!drugInfo) return null
  const { form } = drugInfo as { form?: { id?: string } }
  return form?.id ?? drugInfo.formId ?? null
}

/**
 * Builds the vet diet unit label (form + flavor + packaging).
 * Matches VetDietInfo / SwapProductReactiveForm display.
 */
export const getVetDietUnitLabel = (
  drugInfo: PrescriptionDrugInfo | undefined,
  ProductForm: Constant[],
  PackageType: Constant[],
  InventoryProductSizeUnit: Constant[],
): string => {
  if (!drugInfo) return ''

  const formStr = getManualInputSelectValue(
    ProductForm,
    getFormId(drugInfo),
    drugInfo.customForm,
  )

  // Don't pass saleUnitOfMeasure when it duplicates perPackageUnits (getCommonPackagingLabel appends both; same value => "Can(s) Can(s)").
  const perPackageUnitsResolved = LanguageUtils.getConstantTranslatedName(
    drugInfo.perPackageUnitsId,
    InventoryProductSizeUnit,
  )
  const saleTrimmed = drugInfo.saleUnitOfMeasure?.trim() ?? ''
  const resolvedTrimmed = (perPackageUnitsResolved ?? '').trim()
  const omitSaleUnit =
    saleTrimmed !== '' &&
    resolvedTrimmed !== '' &&
    saleTrimmed.toLowerCase() === resolvedTrimmed.toLowerCase()

  const packageUnitFields = {
    perPackageUnitsId: drugInfo.perPackageUnitsId,
    perPackageAmount: drugInfo.perPackageAmount,
    ...(omitSaleUnit ? {} : { saleUnitOfMeasure: drugInfo.saleUnitOfMeasure }),
  }

  const packaging = getCommonPackagingLabel(
    InventoryProductSizeUnit,
    PackageType,
  )(packageUnitFields)

  return LanguageUtils.getSentence([formStr, drugInfo.flavor, packaging]).trim()
}

export interface QtyRequestedDisplay {
  /** Primary display text (quantity + unit + refills if includeRefills=true) */
  primary: string
  /** Optional secondary text for multiline display (e.g., vet diet description) */
  secondary?: string | null
}

/** Extract quantity value from prescription and parent fallback */
const getQuantityValue = (
  rx: Prescription,
  parentRx?: Prescription,
  usePerRefill?: boolean,
): number => {
  if (usePerRefill) {
    return (
      rx.quantityPerRefill ??
      parentRx?.quantityPerRefill ??
      rx.totalQuantity ??
      parentRx?.totalQuantity ??
      getHighValue(rx.quantity) ??
      getHighValue(parentRx?.quantity) ??
      0
    )
  }
  return (
    rx.totalQuantity ??
    parentRx?.totalQuantity ??
    getHighValue(rx.quantity) ??
    getHighValue(parentRx?.quantity) ??
    0
  )
}

/** Merge prescription with parent data, preserving necessary fields */
const mergeRxWithParent = (
  rx: Prescription,
  parentRx: Prescription,
): Prescription => ({
  ...parentRx,
  ...rx,
  totalQuantityUnitId: rx.totalQuantityUnitId ?? parentRx.totalQuantityUnitId,
  totalQuantityCustomUnit:
    rx.totalQuantityCustomUnit ?? parentRx.totalQuantityCustomUnit,
  quantityPerRefillUnitId:
    rx.quantityPerRefillUnitId ?? parentRx.quantityPerRefillUnitId,
  quantityPerRefillCustomUnit:
    rx.quantityPerRefillCustomUnit ?? parentRx.quantityPerRefillCustomUnit,
  variation: rx.variation ?? parentRx.variation,
  globalVariation: rx.globalVariation ?? parentRx.globalVariation,
  globalVariationMapping:
    rx.globalVariationMapping ?? parentRx.globalVariationMapping,
  rxType: rx.rxType ?? parentRx.rxType,
})

/** Get drugInfo, preferring parent's complete data if child lacks packaging */
const getDrugInfo = (
  rx: Prescription,
  parentRx?: Prescription,
): PrescriptionDrugInfo | undefined => {
  const childHasPackaging =
    rx.drugInfo?.perPackageAmount != null ||
    rx.drugInfo?.perPackageUnitsId != null

  return parentRx?.drugInfo && !childHasPackaging
    ? parentRx.drugInfo
    : (rx.drugInfo ?? parentRx?.drugInfo)
}

/** Check if prescription qualifies for vet diet label */
const shouldShowVetDietLabel = (
  drugInfo: PrescriptionDrugInfo | undefined,
  rxTypeToCheck: string | RxType | undefined,
  vetDietConstants: VetDietConstants | undefined,
): boolean => {
  if (!drugInfo || !vetDietConstants) return false

  const hasVetDietPackaging =
    drugInfo.perPackageAmount != null || drugInfo.perPackageUnitsId != null

  return rxTypeToCheck === RxType.VetDiet || hasVetDietPackaging
}

/** Check if unit is a generic type (unit/item/other) */
export const isGenericUnitType = (unitStr: string): boolean =>
  Boolean(
    unitStr &&
    GENERIC_UNIT_TYPES.includes(
      unitStr.toLowerCase() as (typeof GENERIC_UNIT_TYPES)[number],
    ),
  )

/** Build quantity display for vet diet items */
const buildVetDietQuantity = (
  quantity: number,
  unitStr: string,
  isGeneric: boolean,
  drugInfo: PrescriptionDrugInfo | undefined,
  vetDietConstants: VetDietConstants,
): { quantityUnit: string; secondaryText: string | null } => {
  const vetDietLabel = getVetDietUnitLabel(
    drugInfo,
    vetDietConstants.ProductForm,
    vetDietConstants.PackageType,
    vetDietConstants.InventoryProductSizeUnit,
  )

  if (!vetDietLabel) {
    return { quantityUnit: String(quantity), secondaryText: null }
  }

  if (isGeneric) {
    return {
      quantityUnit: `${quantity} ${unitStr}`,
      secondaryText: vetDietLabel,
    }
  }

  return {
    quantityUnit: `${quantity} ${vetDietLabel}`,
    secondaryText: null,
  }
}

/** Build quantity display for standard prescriptions */
const buildStandardQuantity = (
  quantity: number,
  unitStr: string,
  isGeneric: boolean,
  drugInfo: PrescriptionDrugInfo | undefined,
  vetDietConstants: VetDietConstants | undefined,
): string => {
  let finalUnit = unitStr

  if ((!finalUnit || isGeneric) && drugInfo && vetDietConstants?.ProductForm) {
    const formStr = getManualInputSelectValue(
      vetDietConstants.ProductForm,
      getFormId(drugInfo),
      drugInfo.customForm,
    )
    const formStrString = formStr ? String(formStr).trim() : ''
    if (formStrString) {
      finalUnit = formStrString
    }
  }

  return finalUnit ? `${quantity} ${finalUnit}` : String(quantity)
}

/** Build refill text for display */
const buildRefillText = (quantityNumbRefills: number | undefined): string => {
  if (quantityNumbRefills && quantityNumbRefills > 0) {
    return `${REFILL_SEPARATOR}${i18n.t(
      'Plurals:Z_ICU_WORKAROUND.NUMBER_REFILLS',
      {
        number: quantityNumbRefills,
      },
    )}`
  }
  return `${REFILL_SEPARATOR}${i18n.t('Clients:TIMELINE.PRESCRIPTION_CARD.NO_REFILLS')}`
}

/**
 * Gets the quantity requested display for a prescription with unit and optional refills.
 * Handles vet diet packaging labels and generic unit fallbacks.
 * Returns an object with primary text and optional secondary text for multiline display.
 */
export const getQtyRequested = (
  rx: Prescription,
  options: QuantityAndRefillsOptions = {},
): QtyRequestedDisplay => {
  const {
    includeRefills = true,
    parentRx,
    prescriptionProductSizeUnit = [],
    productSizeUnit = [],
    usePerRefillQuantity = false,
    quantityAndUnitOnly = false,
    vetDietConstants,
  } = options

  const quantity = getQuantityValue(rx, parentRx, usePerRefillQuantity)
  const rxForUnit = parentRx ? mergeRxWithParent(rx, parentRx) : rx
  const drugInfo = getDrugInfo(rx, parentRx)

  // Override unit IDs when using per-refill quantity
  const effectiveRx = usePerRefillQuantity
    ? {
        ...rxForUnit,
        totalQuantityUnitId:
          rxForUnit.quantityPerRefillUnitId ?? rxForUnit.totalQuantityUnitId,
        totalQuantityCustomUnit:
          rxForUnit.quantityPerRefillCustomUnit ??
          rxForUnit.totalQuantityCustomUnit,
      }
    : rxForUnit

  const rxTypeToCheck = parentRx?.rxType ?? effectiveRx.rxType
  const shouldUseVetDietLabel =
    !quantityAndUnitOnly &&
    shouldShowVetDietLabel(
      drugInfo,
      rxTypeToCheck || undefined,
      vetDietConstants,
    )

  const { quantityUnit: standardUnit } = getPrescriptionQuantityUnits(
    effectiveRx,
    {
      PrescriptionProductSizeUnit: prescriptionProductSizeUnit,
      ProductSizeUnit: productSizeUnit,
    },
  )

  const unit = standardUnit?.trim() ?? ''
  const isGeneric = isGenericUnitType(unit)

  let quantityUnit: string
  let secondaryText: string | null = null

  if (shouldUseVetDietLabel && vetDietConstants) {
    const result = buildVetDietQuantity(
      quantity,
      unit,
      isGeneric,
      drugInfo,
      vetDietConstants,
    )
    quantityUnit = result.quantityUnit
    secondaryText = result.secondaryText
  } else {
    quantityUnit = buildStandardQuantity(
      quantity,
      unit,
      isGeneric,
      drugInfo,
      vetDietConstants,
    )
  }

  if (!includeRefills) {
    return { primary: quantityUnit, secondary: secondaryText }
  }

  const refillText = buildRefillText(
    rx.quantityNumbRefills ?? parentRx?.quantityNumbRefills,
  )

  return {
    primary: `${quantityUnit}${refillText}`,
    secondary: secondaryText,
  }
}
